import type { LeadStatus } from '@/lib/api/types';
import type { Permission } from '@/lib/rbac/permissions';

/**
 * Lead-type registry (plan §3.3 / spec §11). One engine serves all four lead types; they
 * differ by status set, columns, and which operations the backend exposes.
 */
export interface LeadTypeConfig {
  key: 'contact' | 'quotes' | 'applications' | 'newsletter' | 'call-requests';
  navKey: string;
  /**
   * English fallback label. Rendered copy is localized via `t(\`nav.${navKey}\`)` at the call
   * site (nav.contact/quotes/applications/newsletter already exist and match these strings).
   */
  label: string;
  /** Statuses offered in the pipeline selector (spec §11.1). */
  statuses: LeadStatus[];
  hasDetail: boolean;
  updatable: boolean; // PATCH status/assignee/notes
  hasExport: boolean;
  hasErasure: boolean; // PDPL DELETE (gap 15)
  /**
   * What the inbox needs to be read (list, detail, export). Every TEPCO inbox is behind
   * `leads:read` today; the field exists so a stricter type (CVs) can be split out without
   * touching the page.
   */
  readPermission: Permission;
}

const CONTACT_STATUSES: LeadStatus[] = [
  'new',
  'in_progress',
  'reviewing',
  'responded',
  'closed',
  'spam',
  'archived',
];

const APPLICATION_STATUSES: LeadStatus[] = [
  'new',
  'in_progress',
  'reviewing',
  'shortlisted',
  'responded',
  'hired',
  'rejected',
  'closed',
  'spam',
  'archived',
];

/** Call requests use a narrower status set than the other queues (PHASE 03). */
const CALL_REQUEST_STATUSES: LeadStatus[] = ['new', 'in_progress', 'closed', 'spam'];

export const LEAD_TYPES: LeadTypeConfig[] = [
  {
    key: 'call-requests',
    navKey: 'call-requests',
    label: 'Call requests',
    statuses: CALL_REQUEST_STATUSES,
    hasDetail: true,
    updatable: true,
    hasExport: true,
    hasErasure: true,
    readPermission: 'leads:read',
  },
  {
    key: 'contact',
    navKey: 'contact',
    label: 'Contact submissions',
    statuses: CONTACT_STATUSES,
    hasDetail: true,
    updatable: true,
    hasExport: true,
    hasErasure: true,
    readPermission: 'leads:read',
  },
  {
    key: 'quotes',
    navKey: 'quotes',
    label: 'Quote requests',
    statuses: CONTACT_STATUSES,
    hasDetail: true,
    updatable: true, // gap 12 resolved; degrades to read-only on 404
    hasExport: true,
    hasErasure: true,
    readPermission: 'leads:read',
  },
  {
    key: 'applications',
    navKey: 'applications',
    label: 'Job applications',
    statuses: APPLICATION_STATUSES,
    hasDetail: true,
    updatable: true,
    hasExport: true,
    hasErasure: true,
    readPermission: 'leads:read',
  },
  {
    key: 'newsletter',
    navKey: 'newsletter',
    label: 'Newsletter',
    statuses: [],
    hasDetail: false,
    updatable: false,
    hasExport: true,
    hasErasure: false,
    readPermission: 'leads:read',
  },
];

export function getLeadType(key: string): LeadTypeConfig | undefined {
  return LEAD_TYPES.find((t) => t.key === key);
}
